/**
 * Strava Connection Drawer — Pixel-parity sibling of GarminDrawer.
 *
 * Every state (Loading, Coming Soon, Connected, Disconnected, Disconnect Confirm)
 * matches GarminDrawer.tsx state-for-state with only the branding swapped.
 * Style values are copied verbatim from GarminDrawer — do not introduce new
 * colors, spacing, or dimensions here.
 *
 * See /web/src/components/settings/GarminDrawer.tsx for the reference.
 */

import React, { useState, useEffect } from 'react';
import { StravaAttribution } from '../StravaAttribution';
import stravaService, { StravaConnectionStatus } from '../../services/stravaService';
import garminService from '../../services/garminService';

interface StravaDrawerProps {
  onClose: () => void;
}

// Branched disconnect-warning wording based on what other sources are connected.
// Branch A: Strava is truly the only source
// Branch B: Only HealthKit/HC is also connected (richest-source case)
// Branch C: Garmin or other rich source connected (low-impact disconnect)
type DisconnectBranch = 'A' | 'B' | 'C';

export const StravaDrawer: React.FC<StravaDrawerProps> = ({ onClose: _onClose }) => {
  const [status, setStatus] = useState<StravaConnectionStatus>({ connected: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnectBranch, setDisconnectBranch] = useState<DisconnectBranch>('C');
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const connectionStatus = await stravaService.getConnectionStatus();
      setStatus(connectionStatus);
    } catch (err) {
      console.error('Failed to load Strava connection status:', err);
      setError('Failed to load Strava connection status');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);
      await stravaService.connect();
      await loadConnectionStatus();
    } catch (err) {
      console.error('Failed to connect Strava:', err);
      setError('Failed to connect to Strava. Please try again.');
    }
  };

  /**
   * Determine the disconnect-warning branch based on what other data sources
   * the user currently has connected.
   */
  const determineDisconnectBranch = async (): Promise<DisconnectBranch> => {
    try {
      const garmin = await garminService.getConnectionStatus();
      if (garmin.connected) {
        return 'C'; // Low-impact: Garmin still flowing
      }
      // We don't have a clean web API for "is HealthKit connected?" — the
      // native app knows via device_sync_status. Conservatively treat as
      // Branch B (richest-source) when no Garmin. Native app can override.
      return 'B';
    } catch {
      return 'A'; // If we can't determine, warn maximally
    }
  };

  const handleShowDisconnectConfirm = async () => {
    const branch = await determineDisconnectBranch();
    setDisconnectBranch(branch);
    setShowDisconnectConfirm(true);
  };

  const handleDisconnect = async () => {
    try {
      setError(null);
      setDisconnecting(true);
      setShowDisconnectConfirm(false);
      await stravaService.disconnect();
      await loadConnectionStatus();
    } catch (err) {
      console.error('Failed to disconnect Strava:', err);
      setError('Failed to disconnect Strava. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  // Loading — mirrors GarminDrawer.tsx:71-79
  if (loading) {
    return (
      <div className="settings-sheet-panel">
        <div className="settings-sheet-panel-title">Strava</div>
        <div className="loading-spinner" style={{ margin: '40px auto', display: 'block' }}>
          Loading connection status...
        </div>
      </div>
    );
  }

  // Disconnecting — same spinner pattern while the deauthorize API call runs
  if (disconnecting) {
    return (
      <div className="settings-sheet-panel">
        <div className="settings-sheet-panel-title">Strava</div>
        <div className="loading-spinner" style={{ margin: '40px auto', display: 'block' }}>
          Disconnecting...
        </div>
      </div>
    );
  }

  return (
    <div className="settings-sheet-panel">
      <div className="settings-sheet-panel-title">Strava</div>

      {/* Brand attribution header — mirrors GarminDrawer lines 86-90 */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ maxWidth: 140, display: 'flex', justifyContent: 'center' }}>
          <StravaAttribution theme="light" size="medium" />
        </div>
      </div>

      {error && (
        <div className="error-message" style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fee',
          color: '#c33',
          borderRadius: '8px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {status.strava_available === false ? (
        /* ── Strava light switch OFF: Coming Soon state ── */
        /* Mirrors GarminDrawer lines 105-134 exactly */
        <div style={{ opacity: 0.6 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: '#f0f0f0',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <span style={{
              fontWeight: '600',
              color: '#888',
              fontSize: '16px'
            }}>
              Coming Soon
            </span>
          </div>
          <p style={{
            color: '#888',
            lineHeight: '1.6',
            fontSize: '15px',
            textAlign: 'center'
          }}>
            Strava integration is currently being finalized and approved by Strava. We'll notify you as soon as it's available.
          </p>
        </div>
      ) : status.connected ? (
        <div className="strava-connected-state">
          {/* Connected badge — mirrors GarminDrawer lines 138-162 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: '#e8f5e9',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <span style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#4caf50',
              display: 'inline-block'
            }}></span>
            <span style={{
              fontWeight: '600',
              color: '#2e7d32',
              fontSize: '16px'
            }}>
              Connected
            </span>
          </div>

          {/* Athlete info block — mirrors Garmin User/Device block at 164-185 */}
          {(status.athlete_name || status.athlete_id) && (
            <div style={{
              marginBottom: '20px',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              fontSize: '15px',
              color: '#333'
            }}>
              {status.athlete_name && (
                <div style={{ marginBottom: status.athlete_id ? '6px' : 0 }}>
                  Strava Athlete: {status.athlete_name}
                </div>
              )}
              {status.athlete_id && (
                <div style={{ fontSize: '14px', color: '#666' }}>
                  Athlete ID: {String(status.athlete_id)}
                </div>
              )}
            </div>
          )}

          {/* Disconnect button — mirrors Garmin's transparent bordered button lines 189-205 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {!showDisconnectConfirm ? (
              <button
                onClick={handleShowDisconnectConfirm}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Disconnect
              </button>
            ) : (
              /* Branched disconnect confirmation — same yellow panel style as GarminDrawer 206-252 */
              <div style={{
                padding: '14px',
                backgroundColor: '#fff3cd',
                borderRadius: '8px',
                border: '1px solid #ffc107'
              }}>
                <div style={{ fontSize: '14px', color: '#856404', marginBottom: '12px', fontWeight: '500' }}>
                  {disconnectBranch === 'A' && (
                    <>⚠️ Strava is your only connected fitness platform. Disconnecting will stop Cedar from receiving any new activities until you connect another source. Are you sure?</>
                  )}
                  {disconnectBranch === 'B' && (
                    <>⚠️ Strava is your richest data source. After disconnecting, Cedar will still see your Peloton, Zwift, and other workouts via Apple Health, but only basic summaries — no power output, no cadence, no instructor names, no segments, no suffer score. Are you sure?</>
                  )}
                  {disconnectBranch === 'C' && (
                    <>Are you sure you want to disconnect from Strava? Your primary device data will keep flowing, but Strava-exclusive fields (segments, suffer score, best efforts) will no longer be added to your activities.</>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleDisconnect}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Disconnect
                  </button>
                  <button
                    onClick={() => setShowDisconnectConfirm(false)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="strava-disconnected-state">
          {/* Disconnected description — mirrors GarminDrawer lines 254-282 */}
          <p style={{
            marginBottom: '24px',
            color: '#666',
            lineHeight: '1.6',
            fontSize: '15px'
          }}>
            Link Strava to automatically sync your Peloton rides, Zwift workouts, segment PRs, and more to Coach Cedar.
          </p>

          {/*
            Official "Connect with Strava" orange button — mandatory per Strava
            brand guidelines. Uses the asset at /strava-assets/. Click handler
            triggers OAuth via the service layer.
          */}
          <button
            onClick={handleConnect}
            style={{
              width: '100%',
              padding: 0,
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            aria-label="Connect with Strava"
          >
            <img
              src="/strava-assets/btn_strava_connect_with_orange.png"
              alt="Connect with Strava"
              style={{ height: 48, width: 'auto', display: 'block' }}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default StravaDrawer;
